import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../config";

import LogoutDialog from "./dialog/logOut";
import { useNotification } from "../notification_context";
import NotiBellImage from "../assets/icons/notibell.jpg";

/* ─── Inline styles as a JS object map ─── */
const S = {
  "@import": `@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Playfair+Display:wght@600&display=swap');`,

  navbar: {
    position: "sticky",
    top: 0,
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 64,
    padding: "0 28px",
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderBottom: "1px solid rgba(0,0,0,0.07)",
    boxShadow: "0 1px 20px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.06)",
    fontFamily: "'DM Sans', sans-serif",
  },

  logo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    flexShrink: 0,
    textDecoration: "none",
  },

  logoMark: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "linear-gradient(135deg, #1976D2 0%, #7B3FF2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Playfair Display', serif",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    flexShrink: 0,
  },

  logoText: {
    fontSize: 19,
    fontWeight: 800,
    color:"black",

  },

  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    background: "#F5F7FA",
    borderRadius: 100,
    padding: 4,
    border: "1px solid rgba(0,0,0,0.07)",
  },

  navLinkBase: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 16px",
    borderRadius: 100,
    fontSize: 13.5,
    fontWeight: 500,
    color: "#5A6478",
    cursor: "pointer",
    border: "none",
    background: "none",
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: "nowrap",
    transition:
      "background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease",
  },

  navLinkActive: {
    background: "#fff",
    color: "#1565C0",
    boxShadow: "0 1px 8px rgba(21,101,192,0.12)",
  },

  navLinkHover: {
    background: "#fff",
    color: "#1565C0",
    boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
  },

  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },

  notiBtn: {
    position: "relative",
    width: 38,
    height: 38,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,0.07)",
    background: "#fff",
    transition: "background 0.18s ease, box-shadow 0.18s ease",
    color: "#5A6478",
    padding: 0,
  },

  notiBadge: {
    position: "absolute",
    top: -5,
    right: -5,
    width: 17,
    height: 17,
    background: "#E53935",
    color: "#fff",
    borderRadius: "50%",
    fontSize: 9,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid #fff",
    fontFamily: "'DM Sans', sans-serif",
  },

  avatarBtn: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "4px 10px 4px 4px",
    borderRadius: 100,
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,0.07)",
    background: "#fff",
    transition: "background 0.18s ease, box-shadow 0.18s ease",
    fontFamily: "'DM Sans', sans-serif",
  },

  avatarCircle: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1976D2, #7B3FF2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
    border: "2px solid rgba(21,101,192,0.15)",
    overflow: "hidden",
  },

  avatarInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  avatarName: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#0D1117",
    lineHeight: 1.2,
  },

  avatarRole: {
    fontSize: 10.5,
    color: "#9BA3B2",
    lineHeight: 1.2,
  },

  dropdown: (open) => ({
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    width: 210,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.07)",
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
    display: open ? "block" : "none",
    animation: open ? "dropIn 0.16s ease" : "none",
    zIndex: 200,
  }),

  dropdownHeader: {
    padding: "14px 16px 10px",
    borderBottom: "1px solid rgba(0,0,0,0.07)",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  dropdownAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1976D2, #7B3FF2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
    overflow: "hidden",
  },

  dropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#5A6478",
    cursor: "pointer",
    border: "none",
    background: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: "'DM Sans', sans-serif",
    transition: "background 0.15s ease, color 0.15s ease",
  },

  dropdownItemDanger: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#C62828",
    cursor: "pointer",
    border: "none",
    background: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: "'DM Sans', sans-serif",
    transition: "background 0.15s ease",
  },

  divider: {
    height: 1,
    background: "rgba(0,0,0,0.07)",
    margin: "2px 0",
  },

  mobileToggle: {
    width: 38,
    height: 38,
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.07)",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#5A6478",
    transition: "background 0.15s ease",
    padding: 0,
  },

  overlay: (open) => ({
    display: open ? "block" : "none",
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    zIndex: 300,
    cursor: "pointer",
    backdropFilter: "blur(2px)",
  }),

  drawer: (open) => ({
    position: "fixed",
    top: 0,
    left: open ? 0 : "-100%",
    width: 290,
    height: "100%",
    background: "#fff",
    boxShadow: "4px 0 40px rgba(0,0,0,0.12)",
    zIndex: 400,
    transition: "left 0.28s cubic-bezier(0.4,0,0.2,1)",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    fontFamily: "'DM Sans', sans-serif",
  }),
};

/* ─── SVG Icon helpers ─── */
const IconHome = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);
const IconStore = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);
const IconSubs = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <path d="M12 22V7" />
    <path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
  </svg>
);
const IconUser = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconBell = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);
const IconLogout = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const IconChevron = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconMenu = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);
const IconClose = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ─── Utility: get initials ─── */
const getInitials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/* ─── Nav items config ─── */
const NAV_ITEMS = [
  { label: "Dashboard", icon: <IconHome />, path: "/insthome" },
  { label: "Store", icon: <IconStore />, path: "/products" },
  { label: "Subscriptions", icon: <IconSubs />, path: "/inst-sub-plans" },
];

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const InstNavbar = () => {
  const navigate = useNavigate();
  const { unreadCount } = useNotification();

  const [profilePic, setProfilePic] = useState("");
  const [name, setName] = useState("Loading...");
  const [activeNav, setActiveNav] = useState("/insthome");
  const [dropOpen, setDropOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);

  const dropdownRef = useRef(null);

  /* Responsive listener */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Fetch profile */
  useEffect(() => {
    const tokenString = localStorage.getItem("authToken");
    if (!tokenString) {
      navigate("/login");
      return;
    }
    try {
      const token = JSON.parse(tokenString);
      fetch(`${API_URL}/student/profile/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      })
        .then((res) => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then((data) => {
          setName(data.name || "LiteUpper");
          setProfilePic(data.profile_pic || "");
        })
        .catch(() => setName("User"));
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const handleNav = (path) => {
    setActiveNav(path);
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    setDropOpen(false);
    setDrawerOpen(false);
    setLogoutOpen(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem("authToken");
    toast.success("Logged out successfully!");
    navigate("/login");
  };

  /* ── Avatar content (image or initials) ── */
  const AvatarContent = ({ size = 30, fontSize = 12 }) =>
    profilePic ? (
      <img
        src={profilePic}
        alt={name}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      <span style={{ fontSize, fontWeight: 600, color: "#fff" }}>
        {name !== "Loading..." ? getInitials(name) : "…"}
      </span>
    );

  return (
    <>
      {/* ── Inject keyframe animation ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Playfair+Display:wght@600&display=swap');
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .inst-nav-link:hover  { background: #fff !important; color: #1565C0 !important; box-shadow: 0 1px 6px rgba(0,0,0,0.07) !important; }
        .inst-drop-item:hover { background: #F5F7FA !important; color: #1565C0 !important; }
        .inst-drop-item:hover svg { color: #1565C0 !important; }
        .inst-drop-danger:hover   { background: #FFF5F5 !important; color: #B71C1C !important; }
        .inst-noti-btn:hover  { background: #F0F4FF !important; border-color: rgba(21,101,192,0.2) !important; box-shadow: 0 2px 8px rgba(21,101,192,0.1) !important; }
        .inst-avatar-btn:hover { background: #F0F4FF !important; box-shadow: 0 2px 10px rgba(21,101,192,0.1) !important; }
        .inst-mobile-toggle:hover { background: #F0F4FF !important; color: #1565C0 !important; }
        .inst-drawer-link:hover { background: #F0F4FF !important; color: #1565C0 !important; }
        .inst-drawer-link:hover svg { color: #1565C0 !important; }
        .inst-drawer-logout:hover { background: #FFF5F5 !important; }
        .inst-close-btn:hover { background: #F5F7FA !important; color: #0D1117 !important; }
      `}</style>

      {/* ══ NAVBAR ══ */}
      <nav style={S.navbar}>
        {/* Logo */}
        <div style={S.logo} onClick={() => handleNav("/insthome")}>
          {/* <div style={S.logoMark}>LU</div> */}
          <span style={S.logoText}>INTITUTION DASHBOARD</span>
        </div>

        {/* Desktop nav links */}
        {!isMobile && (
          <div style={S.navLinks}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.label}
                className="inst-nav-link"
                onClick={() => handleNav(item.path)}
                style={{
                  ...S.navLinkBase,
                  ...(activeNav === item.path ? S.navLinkActive : {}),
                }}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        )}

        {/* Right side */}
        <div style={S.navRight}>
          {/* Notification Bell */}
          <button
            className="inst-noti-btn"
            style={S.notiBtn}
            onClick={() => navigate("/instnotifications")}
            title="Notifications"
          >
            <img
              src={NotiBellImage}
              alt="Notifications"
              style={{ width: 18, height: 18 }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            {unreadCount > 0 && (
              <span style={S.notiBadge}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {/* Avatar + Dropdown (desktop) */}
          {!isMobile && (
            <div ref={dropdownRef} style={{ position: "relative" }}>
              <button
                className="inst-avatar-btn"
                style={S.avatarBtn}
                onClick={() => setDropOpen((v) => !v)}
              >
                <div style={S.avatarCircle}>
                  <AvatarContent size={30} fontSize={12} />
                </div>
                <div style={S.avatarInfo}>
                  <span style={S.avatarName}>{name}</span>
                  <span style={S.avatarRole}>Instructor</span>
                </div>
                <span style={{ color: "#9BA3B2", marginLeft: 2 }}>
                  <IconChevron />
                </span>
              </button>

              {/* Dropdown */}
              <div style={S.dropdown(dropOpen)}>
                {/* Header */}
                <div style={S.dropdownHeader}>
                  <div style={S.dropdownAvatar}>
                    <AvatarContent size={36} fontSize={13} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0D1117",
                      }}
                    >
                      {name}
                    </div>
                    <div style={{ fontSize: 11, color: "#9BA3B2" }}>
                      Instructor
                    </div>
                  </div>
                </div>

                <button
                  className="inst-drop-item"
                  style={S.dropdownItem}
                  onClick={() => {
                    setDropOpen(false);
                    navigate("/instprofile");
                  }}
                >
                  <IconUser /> My Profile
                </button>

                <div style={S.divider} />
                <button
                  className="inst-drop-danger"
                  style={S.dropdownItemDanger}
                  onClick={handleLogout}
                >
                  <IconLogout /> Log Out
                </button>
              </div>
            </div>
          )}

          {/* Hamburger (mobile) */}
          {isMobile && (
            <button
              className="inst-mobile-toggle"
              style={S.mobileToggle}
              onClick={() => setDrawerOpen(true)}
            >
              <IconMenu />
            </button>
          )}
        </div>
      </nav>

      {/* ══ MOBILE OVERLAY ══ */}
      <div style={S.overlay(drawerOpen)} onClick={() => setDrawerOpen(false)} />

      {/* ══ MOBILE DRAWER ══ */}
      <div style={S.drawer(drawerOpen)}>
        {/* Drawer top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 20px 0",
          }}
        >
          <div style={{ ...S.logo, cursor: "default" }}>
            <div style={{ ...S.logoMark, width: 28, height: 28, fontSize: 12 }}>
              LU
            </div>
            <span style={{ ...S.logoText, fontSize: 15 }}>{name}</span>
          </div>
          <button
            className="inst-close-btn"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.07)",
              background: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#9BA3B2",
              transition: "background 0.15s ease",
            }}
            onClick={() => setDrawerOpen(false)}
          >
            <IconClose />
          </button>
        </div>

        {/* Profile card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "20px 20px 16px",
            padding: 14,
            background: "#F5F7FA",
            borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.07)",
          }}
        >
          <div
            style={{ ...S.dropdownAvatar, width: 44, height: 44, fontSize: 15 }}
          >
            <AvatarContent size={44} fontSize={15} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0D1117" }}>
              {name}
            </div>
            <div style={{ fontSize: 11.5, color: "#9BA3B2", marginTop: 1 }}>
              Instructor
            </div>
          </div>
        </div>

        {/* Drawer nav links */}
        <div
          style={{
            padding: "0 14px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.label}
              className="inst-drawer-link"
              onClick={() => handleNav(item.path)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "11px 12px",
                borderRadius: 8,
                fontSize: 13.5,
                fontWeight: 500,
                color: activeNav === item.path ? "#1565C0" : "#5A6478",
                background: activeNav === item.path ? "#F0F4FF" : "none",
                cursor: "pointer",
                border: "none",
                width: "100%",
                textAlign: "left",
                fontFamily: "'DM Sans', sans-serif",
                transition: "background 0.15s ease, color 0.15s ease",
              }}
            >
              <span
                style={{
                  color: activeNav === item.path ? "#1565C0" : "#9BA3B2",
                }}
              >
                {React.cloneElement(item.icon, { width: 16, height: 16 })}
              </span>
              {item.label}
            </button>
          ))}

          <div style={{ ...S.divider, margin: "8px 0" }} />

          {/* Profile link */}
          <button
            className="inst-drawer-link"
            onClick={() => {
              navigate("/instprofile");
              setDrawerOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "11px 12px",
              borderRadius: 8,
              fontSize: 13.5,
              fontWeight: 500,
              color: "#5A6478",
              cursor: "pointer",
              border: "none",
              background: "none",
              width: "100%",
              textAlign: "left",
              fontFamily: "'DM Sans', sans-serif",
              transition: "background 0.15s ease",
            }}
          >
            <span style={{ color: "#9BA3B2" }}>
              <IconUser />
            </span>
            My Profile
          </button>

          {/* Notifications link */}
          <button
            className="inst-drawer-link"
            onClick={() => {
              navigate("/instnotifications");
              setDrawerOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "11px 12px",
              borderRadius: 8,
              fontSize: 13.5,
              fontWeight: 500,
              color: "#5A6478",
              cursor: "pointer",
              border: "none",
              background: "none",
              width: "100%",
              textAlign: "left",
              fontFamily: "'DM Sans', sans-serif",
              transition: "background 0.15s ease",
            }}
          >
            <span style={{ color: "#9BA3B2" }}>
              <IconBell />
            </span>
            Notifications
            {unreadCount > 0 && (
              <span
                style={{
                  marginLeft: "auto",
                  background: "#E53935",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 100,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Logout at bottom */}
        <div style={{ marginTop: "auto" }}>
          <div style={{ ...S.divider, margin: "0 14px 6px" }} />
          <button
            className="inst-drawer-logout"
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "11px 12px",
              borderRadius: 8,
              margin: "0 14px 20px",
              fontSize: 13.5,
              fontWeight: 500,
              color: "#C62828",
              cursor: "pointer",
              border: "none",
              background: "none",
              width: "calc(100% - 28px)",
              textAlign: "left",
              fontFamily: "'DM Sans', sans-serif",
              transition: "background 0.15s ease",
            }}
          >
            <IconLogout /> Log Out
          </button>
        </div>
      </div>

      {/* ══ LOGOUT DIALOG ══ */}
      <LogoutDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default InstNavbar;
